import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Edit, FileText, MapPin, Plus, Printer, Trash2, Upload, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { API_BASE_URL } from '../config';
import { writeSanitizedHTML } from '../utils/safePrint';

const API = `${API_BASE_URL}/api`;

// ---------- Vocabularies ----------
const REPORT_VERSIONS = ['First Report', 'Update Report', 'Final Report'];
const FLOOD_OPTIONS = ['', 'None', 'Minor Flooding', 'Moderate Flooding', 'Major Flooding', 'Flash Flood', 'Coastal Flooding'];
const HAIL_SIZES = [
  'None', 'Pea (¼")', 'Marble (½")', 'Dime (¾")', 'Nickel (⅞")', 'Quarter (1")',
  'Half Dollar (1¼")', 'Ping Pong (1½")', 'Golf Ball (1¾")', 'Tennis Ball (2½")',
  'Baseball (2¾")', 'Softball (4")'
];
const TORNADO_OPTIONS = ['', 'None', 'Funnel Cloud (aloft)', 'Tornado on the ground', 'Waterspout', 'Dust Devil'];
const WIND_DAMAGE_OPTIONS = ['', 'None', 'Tree limbs down', 'Trees down / uprooted', 'Power lines down', 'Roof damage', 'Structural damage', 'Vehicle rolled / moved', 'Debris in roadway'];
const WINTER_PRECIP_OPTIONS = ['', 'None', 'Light snow', 'Heavy snow', 'Blizzard', 'Ice storm', 'Sleet', 'Freezing rain', 'Mixed precipitation'];

// ---------- Validators (mirror WX Report rules) ----------
const TIME_RE = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;
const DEC4_RE = /^-?\d+(\.\d{1,4})?$/;
const validateTime = (v) => (!v ? 'Time is required' : (TIME_RE.test(v) ? '' : 'Use 24-hour HH:MM (e.g. 17:30)'));
const validateLat = (v) => {
  if (v === '' || v === null || v === undefined) return '';
  if (!DEC4_RE.test(String(v))) return 'Decimal to 4 digits (e.g. 47.0000)';
  const n = parseFloat(v);
  if (Number.isNaN(n) || n < -90 || n > 90) return 'LAT must be between -90.0000 and 90.0000';
  return '';
};
const validateLon = (v) => {
  if (v === '' || v === null || v === undefined) return '';
  if (!DEC4_RE.test(String(v))) return 'Decimal to 4 digits (e.g. -97.3456)';
  const n = parseFloat(v);
  if (Number.isNaN(n) || n < -180 || n > 180) return 'LON must be between -180.0000 and 180.0000';
  return '';
};

// ---------- Version pill styling ----------
const VERSION_STYLE = {
  'First Report':  { bg: '#1E3A8A', fg: '#DBEAFE', border: '#3B82F6' },
  'Update Report': { bg: '#854D0E', fg: '#FEF3C7', border: '#EAB308' },
  'Final Report':  { bg: '#166534', fg: '#DCFCE7', border: '#22C55E' },
};

// ---------- Templates ----------
const emptyForm = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return {
    sender: '',
    report_date: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
    report_time: `${pad(now.getHours())}:${pad(now.getMinutes())}`,
    report_version: 'First Report',
    reporting_party_name: '',
    reporting_party_phone: '',
    reporting_party_email: '',
    city: '', state_province_region: '', county: '', other_location: '',
    latitude: '', longitude: '', mgrs: '', grid: '',
    units: 'imperial',
    flood: '',
    hail_size: 'None',
    high_wind_speed: '',
    tornado_funnel_cloud: '',
    wind_damage: '',
    winter_precipitation: '',
    snow: '', freezing_rain: '', heavy_rain: '', time_period_hours: '',
    additional_info: '',
  };
};

const toForm = (rec) => {
  const base = emptyForm();
  const cleaned = Object.fromEntries(
    Object.entries(rec || {}).map(([k, v]) => [k, v === null || v === undefined ? '' : v])
  );
  return { ...base, ...cleaned, units: rec.units || 'imperial', report_version: rec.report_version || 'First Report' };
};

const toPayload = (form) => {
  const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
  return {
    ...form,
    latitude: num(form.latitude),
    longitude: num(form.longitude),
    high_wind_speed: num(form.high_wind_speed),
    snow: num(form.snow),
    freezing_rain: num(form.freezing_rain),
    heavy_rain: num(form.heavy_rain),
    time_period_hours: num(form.time_period_hours),
  };
};

// ---------- Sub-components ----------
const Field = ({ label, children, className = '', error }) => (
  <div className={`space-y-1 ${className}`}>
    <Label className="text-gray-300 text-xs uppercase tracking-wide">{label}</Label>
    {children}
    {error && <div className="text-red-400 text-xs mt-1">{error}</div>}
  </div>
);

const VersionPill = ({ v }) => {
  const s = VERSION_STYLE[v] || VERSION_STYLE['First Report'];
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border"
      style={{ background: s.bg, color: s.fg, borderColor: s.border }}
      data-testid={`sevwx-version-pill-${v.replace(/\s+/g,'-')}`}
    >
      {v}
    </span>
  );
};

// ---------- Print HTML ----------
const buildPrintHTML = (rec) => {
  const esc = (s) => String(s ?? '').replace(/[<>&]/g, (c)=>({ '<':'&lt;','>':'&gt;','&':'&amp;' })[c]);
  const units = rec.units === 'metric' ? { s: 'km/h', d: 'cm' } : { s: 'MPH', d: 'in' };
  const vs = VERSION_STYLE[rec.report_version] || VERSION_STYLE['First Report'];
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Severe Weather Report ${esc(rec.report_date)} ${esc(rec.report_time)}</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; color:#111; margin:20px; }
  h1 { text-align:center; background:#DBEAFE; padding:8px; margin:0 0 10px; font-size:18px; border:1px solid #93C5FD; }
  .banner { display:inline-block; padding:4px 10px; border-radius:12px; font-weight:700; }
  h2 { background:#F3F4F6; padding:5px 8px; margin:12px 0 6px; font-size:13px; border:1px solid #9CA3AF; }
  table { border-collapse: collapse; width:100%; margin-bottom:8px; }
  td, th { border:1px solid #9CA3AF; padding:5px 8px; font-size:12px; vertical-align:top; }
  th { background:#F3F4F6; text-align:left; width:22%; }
  .footer { margin-top:12px; font-size:10px; color:#555; text-align:right; }
  @media print { .no-print { display:none; } }
</style></head><body>
<h1>SEVERE WEATHER REPORT</h1>
<div style="text-align:center;margin-bottom:8px;">
  <span class="banner" style="background:${vs.bg};color:${vs.fg};border:1px solid ${vs.border};">${esc(rec.report_version || 'First Report')}</span>
</div>
<table>
  <tr><th>1. Sender</th><td>${esc(rec.sender)}</td>
      <th>2. Report Date / Time (local)</th><td>${esc(rec.report_date)} ${esc(rec.report_time)}</td></tr>
</table>
<h2>Reporting Party</h2>
<table>
  <tr><th>4. Name</th><td>${esc(rec.reporting_party_name)}</td></tr>
  <tr><th>5. Phone</th><td>${esc(rec.reporting_party_phone)}</td></tr>
  <tr><th>6. Email</th><td>${esc(rec.reporting_party_email)}</td></tr>
</table>
<h2>Event Area</h2>
<table>
  <tr><th>7. City</th><td>${esc(rec.city)}</td>
      <th>8. State / Province / Region</th><td>${esc(rec.state_province_region)}</td></tr>
  <tr><th>9. County</th><td>${esc(rec.county)}</td>
      <th>10. Other</th><td>${esc(rec.other_location)}</td></tr>
  <tr><th>11. Latitude</th><td>${rec.latitude ?? ''}</td>
      <th>12. Longitude</th><td>${rec.longitude ?? ''}</td></tr>
  <tr><th>13. MGRS</th><td>${esc(rec.mgrs)}</td>
      <th>14. Grid</th><td>${esc(rec.grid)}</td></tr>
</table>
<h2>Observed Event Conditions <span style="font-weight:normal;font-size:11px;">(Measurements: ${esc(rec.units || 'imperial')})</span></h2>
<table>
  <tr><th>15. Flood</th><td>${esc(rec.flood)}</td></tr>
  <tr><th>16. Hail size</th><td>${esc(rec.hail_size)}</td></tr>
  <tr><th>17. High Wind Speed</th><td>${rec.high_wind_speed ?? ''} ${units.s}</td></tr>
  <tr><th>18. Tornado / Funnel Cloud</th><td>${esc(rec.tornado_funnel_cloud)}</td></tr>
  <tr><th>19. Wind Damage</th><td>${esc(rec.wind_damage)}</td></tr>
  <tr><th>20. Winter Precipitation</th><td>${esc(rec.winter_precipitation)}</td></tr>
  <tr><th>21. Snow</th><td>${rec.snow ?? ''} ${units.d}</td></tr>
  <tr><th>22. Freezing Rain</th><td>${rec.freezing_rain ?? ''} ${units.d}</td></tr>
  <tr><th>23. Heavy Rain</th><td>${rec.heavy_rain ?? ''} ${units.d}</td>
      <th>24. Time Period</th><td>${rec.time_period_hours ?? ''} hours</td></tr>
</table>
<h2>25. Additional Information / Damage Descriptions</h2>
<div style="border:1px solid #9CA3AF;padding:8px;white-space:pre-wrap;min-height:60px;font-size:12px;">${esc(rec.additional_info)}</div>
<div class="footer">EXRSIM Severe Weather Report · Ver 4.2</div>
<div class="no-print" style="margin-top:10px;text-align:center;"><button onclick="window.print()">Print</button></div>
</body></html>`;
};

// ---------- Main component ----------
const SevereWxModule = ({ theme = 'dark', exerciseId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [fieldErrors, setFieldErrors] = useState({ report_time: '', latitude: '', longitude: '' });
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');
  const fileRef = useRef(null);

  useEffect(() => { if (exerciseId) load(); }, [exerciseId]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/severe-weather-reports?exercise_id=${exerciseId}`);
      setItems(res.data || []);
    } catch (e) { console.error('Load severe wx failed:', e); }
    finally { setLoading(false); }
  };

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (k === 'report_time') setFieldErrors((e) => ({ ...e, report_time: validateTime(v) }));
    if (k === 'latitude') setFieldErrors((e) => ({ ...e, latitude: validateLat(v) }));
    if (k === 'longitude') setFieldErrors((e) => ({ ...e, longitude: validateLon(v) }));
  };

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setFieldErrors({ report_time: '', latitude: '', longitude: '' }); setShowForm(true); };
  const openEdit = (rec) => { setEditing(rec); setForm(toForm(rec)); setFieldErrors({ report_time: '', latitude: '', longitude: '' }); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditing(null); setFieldErrors({ report_time: '', latitude: '', longitude: '' }); };

  const save = async () => {
    if (!exerciseId) { alert('No active exercise'); return; }
    if (!form.report_date || !form.report_time) { alert('Report Date and Time are required'); return; }
    const errs = {
      report_time: validateTime(form.report_time),
      latitude: validateLat(form.latitude),
      longitude: validateLon(form.longitude),
    };
    setFieldErrors(errs);
    if (errs.report_time || errs.latitude || errs.longitude) {
      alert('Please fix highlighted fields:\n' + Object.values(errs).filter(Boolean).join('\n'));
      return;
    }
    try {
      const payload = { ...toPayload(form), exercise_id: exerciseId };
      if (editing?.id) {
        await axios.put(`${API}/severe-weather-reports/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/severe-weather-reports`, payload);
      }
      await load();
      closeForm();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Failed to save report');
    }
  };

  const remove = async (id) => {
    if (!confirm('Delete this severe weather report?')) return;
    try {
      await axios.delete(`${API}/severe-weather-reports/${id}`);
      await load();
    } catch (e) { alert('Delete failed'); }
  };

  const printOne = (rec) => {
    const w = window.open('', '_blank');
    if (!w) { alert('Popup blocked'); return; }
    writeSanitizedHTML(w.document, buildPrintHTML(rec));
    w.focus();
    setTimeout(() => { try { w.print(); } catch(e){} }, 400);
  };

  const useGPS = () => {
    if (!navigator.geolocation) { alert('Geolocation not available'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setForm((f) => ({
        ...f,
        latitude: pos.coords.latitude.toFixed(4),
        longitude: pos.coords.longitude.toFixed(4),
      })),
      (err) => alert('GPS error: ' + err.message)
    );
  };

  const sorted = useMemo(
    () => [...items].sort((a, b) => `${b.report_date} ${b.report_time}`.localeCompare(`${a.report_date} ${a.report_time}`)),
    [items]
  );

  const onImportPick = () => fileRef.current?.click();
  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!exerciseId) { alert('No active exercise'); return; }
    setImporting(true); setImportMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('exercise_id', exerciseId);
      const res = await axios.post(`${API}/severe-weather-reports/import-winlink`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportMsg(`Imported ${res.data.imported} row(s)${res.data.errors?.length ? ' (' + res.data.errors.length + ' skipped)' : ''}`);
      await load();
    } catch (err) {
      console.error(err);
      setImportMsg('Import failed: ' + (err.response?.data?.detail || err.message));
    } finally { setImporting(false); }
  };

  if (!exerciseId) {
    return (
      <div className="p-6 text-gray-300">
        <div className="text-lg font-semibold mb-1">Severe Wx</div>
        <div className="text-sm">Select an active exercise to view or add severe weather reports.</div>
      </div>
    );
  }

  const summary = (rec) => {
    const parts = [];
    if (rec.hail_size && rec.hail_size !== 'None') parts.push(`Hail: ${rec.hail_size}`);
    if (rec.high_wind_speed) parts.push(`Wind ${rec.high_wind_speed}${rec.units === 'metric' ? 'km/h' : 'MPH'}`);
    if (rec.tornado_funnel_cloud && !/^none$/i.test(rec.tornado_funnel_cloud)) parts.push(rec.tornado_funnel_cloud);
    if (rec.flood && !/^none$/i.test(rec.flood)) parts.push(rec.flood);
    return parts.slice(0, 2).join(' · ') || '—';
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="severe-wx-module">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-6 w-6 text-red-400" />
          <h2 className="text-xl md:text-2xl font-bold text-white">Severe Wx</h2>
          <span className="text-gray-400 text-sm">({items.length})</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={onImportPick}
            variant="outline"
            className="border-gray-600 text-gray-200 hover:bg-gray-700"
            data-testid="sevwx-import-winlink-btn"
            disabled={importing}
          >
            <Upload className="h-4 w-4 mr-1" />
            {importing ? 'Importing…' : 'Import Winlink CSV'}
          </Button>
          <Button onClick={openCreate} className="bg-red-600 hover:bg-red-700 text-white" data-testid="sevwx-new-btn">
            <Plus className="h-4 w-4 mr-1" /> New Severe Wx Report
          </Button>
          <input ref={fileRef} type="file" accept=".csv,.txt" onChange={onImportFile} className="hidden" data-testid="sevwx-import-file-input" />
        </div>
      </div>
      {importMsg && <div className="text-sm text-blue-300" data-testid="sevwx-import-msg">{importMsg}</div>}

      {/* Log view */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
        <div className="grid grid-cols-12 px-3 py-2 text-xs font-semibold text-gray-400 uppercase bg-gray-900/60">
          <div className="col-span-3">Date / Time</div>
          <div className="col-span-3">Location</div>
          <div className="col-span-2">Version</div>
          <div className="col-span-2">Summary</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading && <div className="p-4 text-gray-400">Loading…</div>}
        {!loading && sorted.length === 0 && (
          <div className="p-6 text-gray-400 text-center" data-testid="sevwx-empty">
            No severe weather reports yet. Click <span className="text-red-400">New Severe Wx Report</span>.
          </div>
        )}
        {sorted.map((rec) => (
          <div key={rec.id} className="grid grid-cols-12 items-center px-3 py-2 border-t border-gray-700 hover:bg-gray-700/40" data-testid={`sevwx-row-${rec.id}`}>
            <div className="col-span-3 text-gray-100 text-sm font-mono">{rec.report_date} · {rec.report_time}</div>
            <div className="col-span-3 text-gray-200 text-sm truncate">{[rec.city, rec.state_province_region].filter(Boolean).join(', ') || rec.other_location || '—'}</div>
            <div className="col-span-2"><VersionPill v={rec.report_version || 'First Report'} /></div>
            <div className="col-span-2 text-gray-300 text-sm truncate">{summary(rec)}</div>
            <div className="col-span-2 flex justify-end gap-1">
              <Button size="sm" variant="ghost" onClick={() => printOne(rec)} className="text-gray-300 hover:text-white" data-testid={`sevwx-print-${rec.id}`}>
                <Printer className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => openEdit(rec)} className="text-gray-300 hover:text-white" data-testid={`sevwx-edit-${rec.id}`}>
                <Edit className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => remove(rec.id)} className="text-red-400 hover:text-red-300" data-testid={`sevwx-delete-${rec.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-4" data-testid="sevwx-form-modal">
          <div className="bg-gray-900 border border-gray-700 rounded-lg w-full max-w-5xl my-6 shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 sticky top-0 bg-gray-900 rounded-t-lg z-10">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-red-400" />
                <h3 className="text-lg font-semibold text-white">
                  {editing ? 'Edit Severe Weather Report' : 'New Severe Weather Report'}
                </h3>
              </div>
              <button onClick={closeForm} className="text-gray-400 hover:text-white" data-testid="sevwx-form-close">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-6">
              {/* 1-3 Sender / metadata */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="1. Sender">
                  <Input value={form.sender} onChange={(e)=>setField('sender', e.target.value)} placeholder="Call sign / ID" className="bg-gray-800 border-gray-700 text-white" data-testid="sevwx-sender" />
                </Field>
                <Field label="2a. Report Date">
                  <Input type="date" value={form.report_date} onChange={(e)=>setField('report_date', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="sevwx-date" />
                </Field>
                <Field label="2b. Report Time (24h HH:MM)" error={fieldErrors.report_time}>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={5}
                    placeholder="17:30"
                    value={form.report_time}
                    onChange={(e)=>setField('report_time', e.target.value)}
                    className={`bg-gray-800 border text-white ${fieldErrors.report_time ? 'border-red-500' : 'border-gray-700'}`}
                    data-testid="sevwx-time"
                  />
                </Field>
                <Field label="3. Report Version" className="md:col-span-3">
                  <div className="flex flex-wrap gap-2">
                    {REPORT_VERSIONS.map((v) => {
                      const s = VERSION_STYLE[v];
                      const active = form.report_version === v;
                      return (
                        <button
                          key={v}
                          type="button"
                          onClick={() => setField('report_version', v)}
                          className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition ${active ? 'ring-2 ring-white' : 'opacity-70 hover:opacity-100'}`}
                          style={{ background: s.bg, color: s.fg, borderColor: s.border }}
                          data-testid={`sevwx-version-${v.replace(/\s+/g,'-')}`}
                        >
                          {v}
                        </button>
                      );
                    })}
                  </div>
                </Field>
              </div>

              {/* 4-6 Reporting Party */}
              <div className="p-3 rounded border border-gray-700 bg-gray-800/40 space-y-3">
                <div className="text-gray-200 font-semibold">Reporting Party</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Field label="4. Name"><Input value={form.reporting_party_name} onChange={(e)=>setField('reporting_party_name', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="sevwx-rp-name" /></Field>
                  <Field label="5. Phone Number"><Input value={form.reporting_party_phone} onChange={(e)=>setField('reporting_party_phone', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="sevwx-rp-phone" /></Field>
                  <Field label="6. Email Address"><Input type="email" value={form.reporting_party_email} onChange={(e)=>setField('reporting_party_email', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="sevwx-rp-email" /></Field>
                </div>
              </div>

              {/* 7-14 Event Area */}
              <div className="p-3 rounded border border-gray-700 bg-gray-800/40 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-gray-200 font-semibold">Event Area</div>
                  <Button size="sm" onClick={useGPS} variant="outline" className="border-gray-600 text-gray-200" data-testid="sevwx-gps-btn">
                    <MapPin className="h-3.5 w-3.5 mr-1" /> Use GPS
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="7. City"><Input value={form.city} onChange={(e)=>setField('city', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="sevwx-city" /></Field>
                  <Field label="8. State / Province / Region"><Input value={form.state_province_region} onChange={(e)=>setField('state_province_region', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="sevwx-state" /></Field>
                  <Field label="9. County"><Input value={form.county} onChange={(e)=>setField('county', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="sevwx-county" /></Field>
                  <Field label="10. Other"><Input value={form.other_location} onChange={(e)=>setField('other_location', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="sevwx-other" /></Field>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <Field label="11. Latitude" error={fieldErrors.latitude}>
                    <Input
                      value={form.latitude}
                      onChange={(e)=>setField('latitude', e.target.value)}
                      placeholder="44.1042  (-90.0000 to 90.0000)"
                      className={`bg-gray-800 border text-white ${fieldErrors.latitude ? 'border-red-500' : 'border-gray-700'}`}
                      data-testid="sevwx-lat"
                    />
                  </Field>
                  <Field label="12. Longitude" error={fieldErrors.longitude}>
                    <Input
                      value={form.longitude}
                      onChange={(e)=>setField('longitude', e.target.value)}
                      placeholder="-79.7917  (-180.0000 to 180.0000)"
                      className={`bg-gray-800 border text-white ${fieldErrors.longitude ? 'border-red-500' : 'border-gray-700'}`}
                      data-testid="sevwx-lon"
                    />
                  </Field>
                  <Field label="13. MGRS"><Input value={form.mgrs} onChange={(e)=>setField('mgrs', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="sevwx-mgrs" /></Field>
                  <Field label="14. Grid"><Input value={form.grid} onChange={(e)=>setField('grid', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="sevwx-grid" /></Field>
                </div>
                <div className="text-xs text-red-400">LAT/LON default to the center of the grid square unless a GPS is used or LAT/LON/MGRS are entered manually. If sending report for someone else, obtain their coords and enter in decimal format.</div>
              </div>

              {/* Observed Event Conditions */}
              <div className="p-3 rounded border border-gray-700 bg-gray-800/40 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-gray-200 font-semibold">Observed Event Conditions</div>
                  <div className="flex items-center gap-4 text-gray-100">
                    <span className="text-gray-300 text-xs uppercase tracking-wide">Measurements used:</span>
                    <label className="flex items-center gap-2"><input type="radio" name="sevwx-units" checked={form.units==='imperial'} onChange={()=>setField('units','imperial')} data-testid="sevwx-units-imperial"/> Imperial</label>
                    <label className="flex items-center gap-2"><input type="radio" name="sevwx-units" checked={form.units==='metric'} onChange={()=>setField('units','metric')} data-testid="sevwx-units-metric"/> Metric</label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="15. Flood">
                    <select value={form.flood} onChange={(e)=>setField('flood', e.target.value)} className="w-full bg-gray-800 border border-gray-700 text-white rounded px-2 py-2" data-testid="sevwx-flood">
                      {FLOOD_OPTIONS.map((o) => <option key={o} value={o}>{o || 'Choose'}</option>)}
                    </select>
                  </Field>
                  <Field label="16. Hail size">
                    <select value={form.hail_size} onChange={(e)=>setField('hail_size', e.target.value)} className="w-full bg-gray-800 border border-gray-700 text-white rounded px-2 py-2" data-testid="sevwx-hail">
                      {HAIL_SIZES.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label={`17. High Wind Speed (${form.units === 'metric' ? 'km/h' : 'MPH'})`}>
                    <Input value={form.high_wind_speed} onChange={(e)=>setField('high_wind_speed', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="sevwx-wind-speed" />
                  </Field>
                  <Field label="18. Tornado / Funnel Cloud">
                    <select value={form.tornado_funnel_cloud} onChange={(e)=>setField('tornado_funnel_cloud', e.target.value)} className="w-full bg-gray-800 border border-gray-700 text-white rounded px-2 py-2" data-testid="sevwx-tornado">
                      {TORNADO_OPTIONS.map((o) => <option key={o} value={o}>{o || 'Choose'}</option>)}
                    </select>
                  </Field>
                  <Field label="19. Wind Damage">
                    <select value={form.wind_damage} onChange={(e)=>setField('wind_damage', e.target.value)} className="w-full bg-gray-800 border border-gray-700 text-white rounded px-2 py-2" data-testid="sevwx-wind-damage">
                      {WIND_DAMAGE_OPTIONS.map((o) => <option key={o} value={o}>{o || 'Choose'}</option>)}
                    </select>
                  </Field>
                  <Field label="20. Winter Precipitation">
                    <select value={form.winter_precipitation} onChange={(e)=>setField('winter_precipitation', e.target.value)} className="w-full bg-gray-800 border border-gray-700 text-white rounded px-2 py-2" data-testid="sevwx-winter">
                      {WINTER_PRECIP_OPTIONS.map((o) => <option key={o} value={o}>{o || 'Choose'}</option>)}
                    </select>
                  </Field>
                  <Field label={`21. Snow (${form.units === 'metric' ? 'cm' : 'inches'})`}>
                    <Input value={form.snow} onChange={(e)=>setField('snow', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="sevwx-snow" />
                  </Field>
                  <Field label={`22. Freezing Rain (${form.units === 'metric' ? 'cm' : 'inches'})`}>
                    <Input value={form.freezing_rain} onChange={(e)=>setField('freezing_rain', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="sevwx-freezing-rain" />
                  </Field>
                  <Field label={`23. Heavy Rain (${form.units === 'metric' ? 'cm' : 'inches'})`}>
                    <Input value={form.heavy_rain} onChange={(e)=>setField('heavy_rain', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="sevwx-heavy-rain" />
                  </Field>
                  <Field label='24. Time period (hours)'>
                    <Input value={form.time_period_hours} onChange={(e)=>setField('time_period_hours', e.target.value)} placeholder='Report 1" or greater in an hour and every inch thereafter' className="bg-gray-800 border-gray-700 text-white" data-testid="sevwx-time-period" />
                  </Field>
                </div>
              </div>

              <Field label="25. Additional Information or Damage Descriptions (Be Brief)">
                <Textarea value={form.additional_info} onChange={(e)=>setField('additional_info', e.target.value)} rows={4} placeholder="Anything of importance or damages noted. No names or specific addresses." className="bg-gray-800 border-gray-700 text-white" data-testid="sevwx-notes" />
              </Field>
            </div>

            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-700 sticky bottom-0 bg-gray-900 rounded-b-lg">
              <Button variant="outline" onClick={closeForm} className="border-gray-600 text-gray-200 hover:bg-gray-700" data-testid="sevwx-form-cancel">Cancel</Button>
              <Button onClick={save} className="bg-red-600 hover:bg-red-700 text-white" data-testid="sevwx-form-save">
                {editing ? 'Update Report' : 'Save Report'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SevereWxModule;
